// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Sparklight — the crew you play with when nobody else is here.
//
// This file is the single biggest reason the game is or is not worth playing solo. A weak partner in
// a deduction co-op is worse than no partner: every hint you give lands nowhere, and the night reads
// as random rather than as a puzzle you were losing. So this is a real convention-following agent,
// not a card picker.
//
// ── IT CANNOT SEE ITS OWN HAND, AND THAT IS ENFORCED, NOT PROMISED ────────────────────────────────
//
// Every function here takes a VIEW built by `viewFor()`, which masks the seat's own deck positions to
// HIDDEN. The bot then reasons the way a person does: from what it has been told, from what it can
// see in everybody else's hands, and from what has already burned. `tests/bot-view.test.ts` proves
// this rather than trusting it — it re-runs each decision with the bot's own cards swapped for
// different ones and asserts the chosen action does not move. A bot that peeks would change its mind.
//
// ── THE CONVENTION, IN THREE LINES ───────────────────────────────────────────────────────────────
//
// This is also the convention the game TEACHES the player, so it has to fit on a card:
//
//   1. A hint has a FOCUS — the newly-lit card on your chop if there is one, otherwise your newest
//      newly-lit card.
//   2. If the focus is on your chop and could be something irreplaceable, the hint means KEEP IT.
//   3. Otherwise the hint means RAISE IT.
//
// Everything below is bookkeeping in service of those three sentences.

import { HIDDEN, TOP, copiesOf, rankOf, suitOf, touchedByColour } from './cards';
import { suitsOf, type Mode } from './modes';
import {
  A_POINT,
  K_COLOUR,
  K_RANK,
  MAX_HAZE,
  MAX_SPARKS,
  actKind,
  actMask,
  actTarget,
  actValue,
  bit,
  cardAt,
  hasBit,
  isTouched,
  legalHints,
  packDouse,
  packRaise,
  raisable,
  type Game,
  type Known,
} from './game';

// ── difficulty ────────────────────────────────────────────────────────────────────────────────────

export interface Tier {
  id: string;
  name: string;
  blurb: string;
  /**
   * How likely a card it was NOT told about must be before the bot raises it. 1 means "only ever
   * play certainties". This is not the threshold for a clued card: a hint is a promise from a
   * teammate, and a bot that second-guesses one has no convention at all (measured: applying a
   * probability test to clued cards held the crew to 8/25, because a colour hint alone only makes a
   * card 30% likely to be playable and it therefore played nothing it was told to).
   */
  deducedThreshold: number;
  /** Whether it counts cards it can see to narrow a card down. Off, it plays on hints alone. */
  counts: boolean;
  /** Whether it still trusts a play hint when the very last haze is on the line. */
  boldWhenBurning: boolean;
  /** Whether it will spend the last haze on a good bet once the pile is nearly out. */
  gambles: boolean;
  /** Whether it avoids re-telling somebody something they already know. */
  economises: boolean;
}

export const TIERS: Record<string, Tier> = {
  steady: {
    id: 'steady',
    name: 'Steady',
    blurb:
      'Follows the convention but takes no chances — it plays on hints alone and stops trusting them once the sky is nearly gone. A forgiving crew to learn on.',
    deducedThreshold: 1,
    counts: false,
    boldWhenBurning: false,
    gambles: false,
    economises: false,
  },
  sharp: {
    id: 'sharp',
    name: 'Sharp',
    blurb:
      'Counts every card it can see, reads the convention properly and does not waste a spark. Plays the way a good table plays.',
    deducedThreshold: 1,
    counts: true,
    boldWhenBurning: true,
    gambles: false,
    economises: true,
  },
  uncanny: {
    id: 'uncanny',
    name: 'Uncanny',
    blurb:
      'Everything Sharp does, and it will work out a card from what it has NOT been told and spend the last of the haze on a good bet to finish a plume.',
    deducedThreshold: 0.8,
    counts: true,
    boldWhenBurning: true,
    gambles: true,
    economises: true,
  },
};

export const TIER_IDS = ['steady', 'sharp', 'uncanny'];
export const DEFAULT_TIER = 'sharp';
export const tierOf = (id: string | null | undefined): Tier =>
  typeof id === 'string' && Object.hasOwn(TIERS, id) ? TIERS[id] : TIERS[DEFAULT_TIER];

// ── the view ──────────────────────────────────────────────────────────────────────────────────────

/**
 * The game as `seat` is allowed to know it: its own cards blanked out, everything else intact.
 *
 * This is the only door between a Game (which holds every card) and the bot, which is why it is one
 * function rather than a convention about what to read. `events` is carried through because the
 * convention is derived from the public history; it contains no card the seat may not see, because
 * a card only enters an event once it has left somebody's hand.
 */
export function viewFor(g: Game, seat: number): Game {
  /**
   * ONLY A PEER THAT HOLDS THE DECK MAY DRIVE A BOT, AND THIS IS WHY IT CANNOT BE OTHERWISE.
   *
   * The bot for seat S needs to see every hand except S's — which includes the hand of whoever is
   * running it. A guest does not know its own cards, so it physically cannot build a correct view
   * for anybody else: it would produce a plausible, different action from the one the dealer
   * produced, and the crew would be playing two games.
   *
   * So the rule "only the dealer runs bots" is not a convention to remember, it is a precondition
   * that throws. A silent wrong answer here would be indistinguishable from a bot that is merely
   * playing badly, which is exactly the kind of bug that ships.
   */
  for (let s = 0; s < g.setup.players; s++) {
    if (s === seat) continue;
    for (const p of g.hands[s]) {
      if (g.seen[p] === HIDDEN) {
        throw new Error(`viewFor(${seat}): seat ${s} is not visible — only the peer holding the deck may drive a bot`);
      }
    }
  }
  const seen = g.seen.slice();
  for (const p of g.hands[seat]) seen[p] = HIDDEN;
  return {
    ...g,
    seen,
    hands: g.hands.map((h) => [...h]),
    known: g.known.map((ks) => ks.map((k) => ({ ...k }))),
    plumes: [...g.plumes],
    ash: [...g.ash],
    log: [...g.log],
    events: [...g.events],
  };
}

// ── what a card could still be ────────────────────────────────────────────────────────────────────

/** Every distinct card in this mode, with how many copies the deck holds. */
function composition(mode: Mode): Array<{ card: number; copies: number }> {
  const out: Array<{ card: number; copies: number }> = [];
  for (const s of suitsOf(mode)) {
    for (let r = 1; r <= TOP; r++) out.push({ card: s.id * 8 + r, copies: copiesOf(s, r) });
  }
  return out;
}

const compCache = new Map<string, Array<{ card: number; copies: number }>>();
function compositionOf(mode: Mode): Array<{ card: number; copies: number }> {
  let c = compCache.get(mode.id);
  if (!c) {
    c = composition(mode);
    compCache.set(mode.id, c);
  }
  return c;
}

/** Does a card satisfy everything this slot has been told? */
function fits(k: Known, c: number, g: Game): boolean {
  for (let colour = 0; colour < 6; colour++) {
    const touched = touchedByColour(c, colour, g.suits);
    if (hasBit(k.yes, colour) && !touched) return false;
    if (hasBit(k.no, colour) && touched) return false;
  }
  const r = rankOf(c);
  if (k.rankYes !== 0 && !hasBit(k.rankYes, r)) return false;
  if (hasBit(k.rankNo, r)) return false;
  return true;
}

export interface Candidates {
  /** card -> how many copies could still be sitting here, weighted for the probability sums. */
  weights: Map<number, number>;
  total: number;
}

/**
 * What the card in `slot` could be, and how likely each option is.
 *
 * `counts` is the difference between a bot that plays off hints alone and one that plays like a
 * person: with it on, every copy of a card that is already visible in somebody's hand, in the ash or
 * under a raised plume is struck off, which is most of the deduction in a real game.
 */
export function candidatesFor(g: Game, seat: number, slot: number, counts: boolean): Candidates {
  const k = g.known[seat][slot];
  const weights = new Map<number, number>();
  let total = 0;
  const gone = counts ? accountedFor(g, seat) : null;
  for (const { card, copies } of compositionOf(g.setup.mode)) {
    if (!fits(k, card, g)) continue;
    const left = gone ? copies - (gone.get(card) ?? 0) : copies;
    if (left <= 0) continue;
    weights.set(card, left);
    total += left;
  }
  return { weights, total };
}

/** Every copy of every card this seat can actually see, so it can strike them off its own options. */
function accountedFor(g: Game, seat: number): Map<number, number> {
  const gone = new Map<number, number>();
  const add = (c: number): void => {
    if (c !== HIDDEN) gone.set(c, (gone.get(c) ?? 0) + 1);
  };
  for (const c of g.ash) add(c);
  for (const s of g.suits) {
    for (let r = 1; r <= g.plumes[s.id]; r++) add(s.id * 8 + r);
  }
  for (let s = 0; s < g.setup.players; s++) {
    if (s === seat) continue;
    for (const p of g.hands[s]) add(cardAt(g, p));
  }
  return gone;
}

/** The chance the card in this slot is raisable right now. */
export function chancePlayable(g: Game, seat: number, slot: number, counts: boolean): number {
  const cs = candidatesFor(g, seat, slot, counts);
  if (cs.total === 0) return 0;
  let good = 0;
  for (const [c, w] of cs.weights) if (raisable(g, c)) good += w;
  return good / cs.total;
}

/** True when every card it could be has already been raised — so losing it costs nothing. */
export function isDead(g: Game, seat: number, slot: number, counts: boolean): boolean {
  const cs = candidatesFor(g, seat, slot, counts);
  if (cs.total === 0) return false;
  for (const c of cs.weights.keys()) if (g.plumes[suitOf(c)] < rankOf(c)) return false;
  return true;
}

/** How many copies of this exact card the crew has left. Zero means the plume can never be finished. */
export function copiesRemaining(g: Game, c: number): number {
  const spec = g.suits.find((s) => s.id === suitOf(c));
  if (!spec) return 0;
  let left = copiesOf(spec, rankOf(c));
  for (const x of g.ash) if (x === c) left--;
  return Math.max(0, left);
}

/** A card that still matters and of which exactly one copy survives. Losing it caps a plume forever. */
export function isCritical(g: Game, c: number): boolean {
  return c !== HIDDEN && g.plumes[suitOf(c)] < rankOf(c) && copiesRemaining(g, c) === 1;
}

// ── chop, and the convention ──────────────────────────────────────────────────────────────────────

/**
 * The card this seat would throw away next: the oldest one nobody has said anything about. -1 when
 * every card has been touched, which is a seat with nothing safe to lose.
 */
export function chopOf(g: Game, seat: number): number {
  const ks = g.known[seat];
  for (let i = 0; i < ks.length; i++) if (!isTouched(ks[i])) return i;
  return -1;
}

export type Intent = 'play' | 'save';

/**
 * Walk the public history and work out what every hint MEANT, keyed by deck position.
 *
 * Keyed by position rather than by slot on purpose: slots shift every time a card leaves a hand, and
 * an intent that slid one place along is an intent pointing at the wrong card. A position is the one
 * identifier in this game that never moves.
 */
export function intentsFrom(g: Game): Map<number, Intent> {
  const out = new Map<number, Intent>();
  const hands: number[][] = Array.from({ length: g.setup.players }, () => []);
  const touched = new Set<number>();

  for (const e of g.events) {
    if (e.k === 'draw') {
      hands[e.seat].push(e.pos);
      continue;
    }
    if (e.k === 'raise' || e.k === 'douse') {
      hands[e.seat].splice(e.slot, 1);
      continue;
    }
    if (e.k !== 'point') continue;

    const hand = hands[e.target] ?? [];
    // Chop BEFORE the hint — the oldest card nobody had said anything about. That is the card the
    // receiver was about to throw, which is the only reason a hint can be a warning at all.
    const chop = hand.find((p) => !touched.has(p)) ?? -1;
    const lit = e.slots.map((i) => hand[i]).filter((p) => p !== undefined);
    const fresh = lit.filter((p) => !touched.has(p));
    for (const p of lit) touched.add(p);
    if (fresh.length === 0) continue;

    // The focus: the newly-lit card on chop if the hint reached it, otherwise the newest newly-lit
    // card. `hand` is oldest-first, so "newest" is simply the last one.
    const focus = fresh.includes(chop) ? chop : fresh[fresh.length - 1];
    out.set(focus, readIntent(e.kind, focus === chop));
  }
  return out;
}

/**
 * THE WHOLE CONVENTION, in one expression, used by BOTH sides.
 *
 * A rank hint that reaches the card you were about to throw means KEEP IT. Anything else means RAISE
 * IT. Reader and writer call this same function, which is the only way to be sure the crew is playing
 * the same game — an agent whose "what did that mean" and "what will they think this means" are two
 * separate pieces of code will eventually disagree with itself, and the player pays for it.
 */
export const readIntent = (kind: number, onChop: boolean): Intent =>
  kind === K_RANK && onChop ? 'save' : 'play';

// ── choosing ──────────────────────────────────────────────────────────────────────────────────────

export interface Decision {
  action: number;
  why: string;
}

/**
 * The bot's whole turn, in the order a person would think about it: can I raise something, does
 * somebody need telling, can I afford to lose something, and only then, what do I throw.
 */
export function decide(view: Game, seat: number, tier: Tier, jitter = 0): Decision {
  const intents = intentsFrom(view);
  const hand = view.hands[seat];
  const risky = view.haze >= MAX_HAZE - 1;
  const endgame = view.deckSize - view.drawn <= view.setup.players;

  /**
   * 1. Raise something.
   *
   * TWO ROUTES, AND THEY ARE NOT THE SAME. A card a teammate pointed at is TRUSTED — played unless
   * this seat can prove it is not playable — because that is what makes the hint worth a spark. A
   * card the bot merely worked out for itself has to clear the tier's threshold, because nobody put
   * their name to it. Getting this the wrong way round is what held the first build to 8/25: it
   * applied a probability test to clued cards, a colour hint alone only makes a card about 30%
   * likely to be playable, and so the crew spent 25 sparks a night and raised almost nothing.
   */
  let told = -1;
  let toldOldest = Infinity;
  let deduced = -1;
  let deducedP = 0;
  for (let i = 0; i < hand.length; i++) {
    const p = chancePlayable(view, seat, i, tier.counts);
    if (p <= 0) continue; // provably not playable: the hint has been overtaken by events
    if (intents.get(hand[i]) === 'play') {
      // A cautious crew stops trusting when the third haze would end the night.
      if (risky && !tier.boldWhenBurning && p < 1) continue;
      if (hand[i] < toldOldest) {
        toldOldest = hand[i];
        told = i;
      }
    } else if (p >= tier.deducedThreshold && p > deducedP) {
      deducedP = p;
      deduced = i;
    }
  }
  // Oldest instruction first: hints were given in an order, and playing them out of it strands the
  // card somebody built the next hint around.
  if (told >= 0) return { action: packRaise(seat, told), why: 'told' };
  if (deduced >= 0) return { action: packRaise(seat, deduced), why: 'deduced' };

  // 2. Say something, if there is anything worth a spark.
  if (view.sparks > 0) {
    const hint = bestHint(view, seat, tier, jitter);
    if (hint) return { action: hint.action, why: hint.why };
  }

  /**
   * 2b. THE STALL. With a full pool there is nothing to douse — the rules forbid it — so a seat with
   * no play and nothing useful to say would otherwise fall through to raising a card at random and
   * burning a haze for it. Measured: that alone ended 99% of two-player nights on haze inside 26
   * turns, with three failed raises every time.
   *
   * A hint that lights only cards somebody has already been told about creates no new focus, so it
   * carries no instruction and cannot be misread. It costs a spark and buys a turn, which is exactly
   * what is needed, and it is what a person does at a table too.
   */
  if (view.sparks > 0 && view.sparks >= MAX_SPARKS) {
    const stall = stallHint(view, seat);
    if (stall >= 0) return { action: stall, why: 'stall' };
  }

  // 3. Throw away something that cannot matter.
  for (let i = 0; i < hand.length; i++) {
    if (view.sparks < MAX_SPARKS && isDead(view, seat, i, tier.counts)) {
      return { action: packDouse(seat, i), why: 'dead' };
    }
  }

  // 4. Throw the chop.
  if (view.sparks < MAX_SPARKS) {
    const chop = chopOf(view, seat);
    if (chop >= 0) return { action: packDouse(seat, chop), why: 'chop' };
    // Everything has been spoken for. Lose the least-informative card rather than gambling a haze.
    const safest = leastValuable(view, seat, tier);
    if (safest >= 0) return { action: packDouse(seat, safest), why: 'squeezed' };
  }

  // 5. Cornered: no sparks to give back and nothing safe to say. Take the best bet on the table.
  let gamble = 0;
  let gambleP = -1;
  for (let i = 0; i < hand.length; i++) {
    const p = chancePlayable(view, seat, i, tier.counts);
    if (p > gambleP) {
      gambleP = p;
      gamble = i;
    }
  }
  if (tier.gambles && endgame && gambleP > 0) return { action: packRaise(seat, gamble), why: 'gamble' };
  return { action: packRaise(seat, gamble), why: 'forced' };
}

/**
 * A hint that instructs nobody: every card it lights has already been spoken for, so no new focus
 * exists and the convention stays silent. Returns -1 when there is no such hint, which happens on a
 * table where nothing has been said yet.
 */
export function stallHint(g: Game, seat: number): number {
  let fallback = -1;
  for (const action of legalHints(g, seat)) {
    const target = actTarget(action);
    const mask = actMask(action);
    const hand = g.hands[target];
    let fresh = 0;
    for (let i = 0; i < hand.length; i++) {
      if (mask & (1 << i) && !isTouched(g.known[target][i])) fresh++;
    }
    if (fresh === 0) return action;
    // Nothing perfectly silent on offer. The least-bad stall lights the FEWEST new cards, and we
    // keep looking for a silent one rather than taking this immediately.
    if (fallback === -1) fallback = action;
  }
  return fallback;
}

/** The slot whose loss costs least: lowest chance of being critical, then oldest. */
function leastValuable(g: Game, seat: number, tier: Tier): number {
  let best = -1;
  let bestRisk = Infinity;
  for (let i = 0; i < g.hands[seat].length; i++) {
    const cs = candidatesFor(g, seat, i, tier.counts);
    let risk = 0;
    for (const [c, w] of cs.weights) if (isCritical(g, c)) risk += w;
    const norm = cs.total === 0 ? 1 : risk / cs.total;
    if (norm < bestRisk) {
      bestRisk = norm;
      best = i;
    }
  }
  return best;
}

interface Scored {
  action: number;
  score: number;
  why: string;
}

/**
 * The best thing to say, or nothing.
 *
 * The discipline that makes the whole convention work lives here: a hint whose focus is NOT playable
 * and NOT a save is scored below zero and never given. Give one of those and the receiver dutifully
 * raises a card that burns a haze — which is far worse than saying nothing at all, and is the way a
 * naive "touch as many cards as possible" bot destroys a run.
 */
export function bestHint(g: Game, seat: number, tier: Tier, jitter = 0): Scored | null {
  const intents = intentsFrom(g);
  const options = legalHints(g, seat);
  let best: Scored | null = null;

  for (const action of options) {
    const target = actTarget(action);
    const kind = actKind(action);
    const value = actValue(action);
    const mask = actMask(action);
    const hand = g.hands[target];

    const lit: number[] = [];
    for (let i = 0; i < hand.length; i++) if (mask & (1 << i)) lit.push(i);
    const fresh = lit.filter((i) => !isTouched(g.known[target][i]));
    if (fresh.length === 0 && tier.economises) continue;

    const chop = chopOf(g, target);
    const focus = fresh.includes(chop) ? chop : fresh.length > 0 ? fresh[fresh.length - 1] : lit[lit.length - 1];
    const focusCard = cardAt(g, hand[focus]);
    if (focusCard === HIDDEN) continue;

    const onChop = focus === chop;
    // How the receiver WILL read it — the same function they were taught, never a second opinion.
    const reads = readIntent(kind, onChop);
    const wantsSave = onChop && isCritical(g, focusCard) && !raisable(g, focusCard);
    const wantsPlay = raisable(g, focusCard) && intents.get(hand[focus]) !== 'play';

    let s = -1000;
    if (wantsPlay && reads === 'play') {
      // Urgency: telling the player who acts next is worth more than telling the one who acts last.
      const distance = (target - seat + g.setup.players) % g.setup.players;
      s = 100 - distance * 6;
      // Low cards first — a 1 unlocks a plume, a 4 unlocks nothing yet.
      s += (TOP - rankOf(focusCard)) * 2;
      // A hint that also legitimately marks other playable cards is worth more.
      for (const i of fresh) {
        if (i === focus) continue;
        const c = cardAt(g, hand[i]);
        if (c !== HIDDEN && raisable(g, c)) s += 4;
      }
      // Do not tell somebody something they can already act on.
      if (chancePlayable(g, target, focus, tier.counts) >= 0.999) s -= 60;
    } else if (wantsSave && reads === 'save') {
      // A card about to be thrown that can never be replaced. The most urgent thing in the game —
      // and the more so the sooner that seat acts.
      const distance = (target - seat + g.setup.players) % g.setup.players;
      s = 140 - distance * 4;
      if (value === rankOf(focusCard)) s += 6;
    }

    /**
     * NEVER MISLEAD, and this `continue` is where that is enforced.
     *
     * Two ways a hint lies, and both are here: the focus is neither playable nor a genuine save (so
     * "raise it" burns a haze on our own advice), or the focus IS one of those but the shape of the
     * hint reads as the other one (a rank hint to a chop card that we meant as a play). Saying
     * nothing is always better than either. A bot that scores hints by how many cards they touch
     * gives both kinds constantly, and a crew following it faithfully loses every run.
     */
    if (!(wantsPlay && reads === 'play') && !(wantsSave && reads === 'save')) continue;

    // Redundant information is a spark spent on nothing.
    if (tier.economises) {
      let novel = 0;
      for (const i of fresh) {
        const k = g.known[target][i];
        if (kind === K_COLOUR ? !hasBit(k.yes, value) : !hasBit(k.rankYes, value)) novel++;
      }
      if (novel === 0) s -= 30;
    }
    if (jitter) s += jitter * (((action * 2654435761) % 1000) / 1000 - 0.5);

    if (s > -900 && (best === null || s > best.score)) {
      best = { action, score: s, why: wantsSave ? 'save' : 'play' };
    }
  }

  // Sparks are not free late on: with one left and nothing urgent, holding it is better than
  // spending it on a marginal hint that nobody can act on.
  if (best && best.score < 40 && g.sparks <= 1) return null;
  return best;
}

/** The keys a BotView is permitted to expose, asserted by tests/bot-view.test.ts. */
export const BOT_READS = ['seen', 'hands', 'known', 'plumes', 'ash', 'sparks', 'haze', 'events'] as const;

export { A_POINT, MAX_HAZE, bit };
